/** Web Push subscription (PWA); native iOS/Android uses a separate FCM/APNs track later. */

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export type VapidConfig = {
  enabled: boolean
  public_key: string
}

export async function fetchVapidPublicKey(): Promise<VapidConfig> {
  const res = await fetch('/api/notifications/vapid-public-key/', { credentials: 'include' })
  if (!res.ok) {
    throw new Error(`VAPID config failed: ${res.status}`)
  }
  return res.json()
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    return null
  }
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  } catch (e) {
    console.warn('[Push] service worker registration failed', e)
    return null
  }
}

export async function subscribeToWebPush(vapidPublicKey: string): Promise<PushSubscription | null> {
  if (!('PushManager' in window) || !vapidPublicKey) {
    return null
  }
  const registration = await registerServiceWorker()
  if (!registration) {
    return null
  }

  let subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    })
  }

  const json = subscription.toJSON()
  const keys = json.keys as { p256dh?: string; auth?: string } | undefined
  if (!json.endpoint || !keys?.p256dh || !keys?.auth) {
    throw new Error('Invalid push subscription keys')
  }

  const res = await fetch('/api/notifications/push-subscriptions/', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: { p256dh: keys.p256dh, auth: keys.auth },
      user_agent: navigator.userAgent,
    }),
  })
  if (!res.ok) {
    throw new Error(`Push subscription save failed: ${res.status}`)
  }
  return subscription
}

export async function unsubscribeFromWebPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return
  }
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) {
    return
  }
  const json = subscription.toJSON()
  if (json.endpoint) {
    await fetch('/api/notifications/push-subscriptions/', {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys || {} }),
    })
  }
  await subscription.unsubscribe()
}

export function isPushSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator
}
